import {
  deleteVersicherungsnehmerExceptisFinanzdienstleister,
  GesamtpreisChanged,
  GlobalAction,
  GlobalActions,
  isAngebotsIdEqual,
  isAzam,
  NavigationRoute,
  ReplaceState,
  Treuebonus,
  WrappedSavedStates,
  WrappedSavedStatesLoaded,
} from '@ps20-frontend/shop-integration-redux';
import { AnyAction, Reducer } from 'redux';
import cloneDeep from 'lodash/cloneDeep';
import {
  App,
  AppSubroute,
  getInactiveProductApps,
  getProtectedAngebotsIds,
  isEntryProtectedOrActive,
} from '@ps20-frontend/frontend-models';
import { Feature, Tarifbereich, Zahlungsart } from '@ps20/bff-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { NachlaesseForAngebot } from '@ps20/bff-models/dist/lib/ps20-bff-model-taas';
import { loadAndMigrateToolbarState } from '../migration';
import { VnRoleEnum } from '../interfaces/versicherungsnehmer';
import { VorteilsrabattProdukt } from '../interfaces/vorteilsrabatt';
import {
  getProgressbarItemIdForRoute,
  getProgressbarItemsForApp,
  ProgressbarItemsEnum,
} from '../../app/progressbar/data/progressbarItems';
import { IAppState } from '../toolbarState';
import { ToolbarActions } from '../toolbar.actions';
import { transientStateReducer } from './transient-state.reducer';

export const rootReducer =
  (reducer: Reducer) =>
  (lastState: IAppState, action: AnyAction): IAppState => {
    switch (action.type) {
      case GlobalActions.SAVED_STATES_LOADED: {
        const newState = cloneDeep(lastState);
        const actionPayload: WrappedSavedStates = (action as GlobalAction<WrappedSavedStatesLoaded>)
          .payload.savedStatesLoaded;
        const loadedAppState = actionPayload.find(
          loadedState => loadedState.appId === App.SHOP_TOOLBAR,
        );

        if (loadedAppState != null) {
          if (isAzam(newState.transientState.role)) {
            deleteVersicherungsnehmerExceptisFinanzdienstleister(
              loadedAppState.state.versicherungsnehmer,
            );
            if (newState.zahlungsoptionen?.zahlungsart === Zahlungsart.MAKLER_INKASSO) {
              delete loadedAppState.state?.zahlungsoptionen?.zahlungsart;
            }
          }
          const migratedToolbarState = loadAndMigrateToolbarState(
            newState,
            loadedAppState.state,
          );

          const inactiveApps = getInactiveProductApps(
            newState.transientState.featureToggles as { [key: string]: boolean },
          );
          const protectedAngebotsIds = getProtectedAngebotsIds(actionPayload, App.SHOP_CHECKOUT);
          migratedToolbarState.treuebonus = (migratedToolbarState.treuebonus ?? []).filter((t: Treuebonus) =>
            isEntryProtectedOrActive(t, inactiveApps, protectedAngebotsIds),
          );
          if (migratedToolbarState.vorteilsrabatt?.produkte) {
            migratedToolbarState.vorteilsrabatt.produkte = migratedToolbarState.vorteilsrabatt.produkte.filter((p: VorteilsrabattProdukt) =>
              isEntryProtectedOrActive(p, inactiveApps, protectedAngebotsIds),
            );
          }

          const versicherungsnehmer = migratedToolbarState.versicherungsnehmer;
          if (versicherungsnehmer.isAssekuranztarif === true && !versicherungsnehmer.tarifbereich) {
            versicherungsnehmer.currentRole = VnRoleEnum.MITARBEITER;
            versicherungsnehmer.tarifbereich = Tarifbereich.A;
          }
          if (
            lastState.transientState.isSdwSession &&
            lastState.transientState.featureToggles[Feature.VTNR_AMISNOW] &&
            loadedAppState.state.accountManager
          ) {
            migratedToolbarState.transientState.vermittlernummer =
              loadedAppState.state.accountManager;
          }
          return migratedToolbarState;
        } else {
          return lastState;
        }
      }
      case GlobalActions.LOCATION_CHANGED:
      case GlobalActions.NAVIGATION_COMPLETED: {
        const actionPayload: NavigationRoute = action.payload;
        const aktivesMenuItem = getProgressbarItemIdForRoute(
          actionPayload.appId,
          actionPayload.subroute,
        );
        const menuItems = getProgressbarItemsForApp(actionPayload.appId);
        const toolbarIsInteractive = actionPayload.subroute !== AppSubroute.SHOP_CHECKOUT_DANKE;

        const updatedState: Partial<IAppState> = {
          toolbarIsInteractive,
          aktiveRubrik: aktivesMenuItem,
          rubriken: menuItems,
          transientState: transientStateReducer(lastState.transientState, action, lastState),
        };
        const newState = { ...lastState, ...updatedState };
        if (shouldHideGesamtpreis(aktivesMenuItem)) {
          newState.gesamtpreisHidden = true;
        } else if (shouldNotHideGesamtpreis(action.payload.appId)) {
          newState.gesamtpreisHidden = false;
        }
        if (action.type === GlobalActions.NAVIGATION_COMPLETED) {
          newState.gesamtpreisPopover.gesamtpreisInfoAllowed = isGesamtpreisInfoAllowed(
            lastState.aktiveRubrik,
            aktivesMenuItem,
          );
        }
        newState.aktivesMenuItem = aktivesMenuItem;
        return newState;
      }
      case GlobalActions.VORVERTRAG_BEIBEHALTEN:
      case GlobalActions.TARIFIERUNG_HAS_STARTED: {
        const newState = cloneDeep(lastState);
        newState.transientState.tarifierungIsInProgress = true;
        if (lastState.gesamtpreisHidden) {
          newState.gesamtpreisHidden = false;
        }
        return newState;
      }
      case GlobalActions.GESAMTPREIS_CHANGED: {
        const newState: IAppState = cloneDeep(lastState);
        const actionPayload: GesamtpreisChanged = action.payload;
        if (actionPayload.gesamtpreis == null && lastState.gesamtpreis != null) {
          PS20Logger.info(
            'SHOP_TOOLBAR.gesamtpreisChanged.emptyPriceIgnored',
            JSON.stringify({
              triggeredByApp: actionPayload.triggeredByApp,
              angebotsId: actionPayload.angebotsId,
              keptGesamtpreis: lastState.gesamtpreis,
            }),
            App.SHOP_TOOLBAR,
          );
        } else {
          newState.gesamtpreis = actionPayload.gesamtpreis;
        }
        if (shouldShowGesamtpreisInfo(lastState, newState, actionPayload)) {
          newState.gesamtpreisPopover.gesamtpreisInfoChanged = true;
        }
        newState.gesamtpreisPopover.gesamtpreisInfoAllowed = true;
        return newState;
      }
      case GlobalActions.REPLACE_STATE: {
        const actionPayload: ReplaceState<IAppState> = action.payload;
        if (actionPayload.appId === App.SHOP_TOOLBAR) {
          return withLiveTiergruppeTakeOver(actionPayload.appState, lastState);
        }
        return lastState;
      }
      case GlobalActions.OBJEKT_MIT_AUSLAENDISCHE_ADRESSE_ADDED: {
        const neueObjektImAusland = action.payload;
        const listOfProdukteImAusland = lastState.produkteImAuslandListe;
        if (
          listOfProdukteImAusland != null &&
          listOfProdukteImAusland.some(c => isAngebotsIdEqual(c, neueObjektImAusland))
        ) {
          listOfProdukteImAusland.find(
            objektImAusland =>
              objektImAusland.appId === neueObjektImAusland.appId &&
              objektImAusland.angebotsId === neueObjektImAusland.angebotsId,
          ).containsAuslaendischeAdresse = neueObjektImAusland.containsAuslaendischeAdresse;
        } else {
          lastState.produkteImAuslandListe.push(neueObjektImAusland);
        }
        return cloneDeep(lastState);
      }
      case GlobalActions.AUSLAENDISCHE_ADRESSE_ANGEBOT_REMOVED: {
        const test = action.payload;
        const listOfProdukteImAusland = lastState.produkteImAuslandListe;
        lastState.produkteImAuslandListe = listOfProdukteImAusland.filter(
          objektImAusland => !isAngebotsIdEqual(objektImAusland, test),
        );
        return cloneDeep(lastState);
      }
      case ToolbarActions.RABATT_SERVICE_NACHLAESSE_RECEIVED: {
        lastState.receivedNachlaesseForAlleAngebote = action.payload as Array<NachlaesseForAngebot>;
        return lastState;
      }
      case GlobalActions.SET_ANFRAGEPFLICHTIGE_INFORMATIONEN: {
        return {
          ...lastState,
          toolbarIsInteractive: !action.payload.isAnfragepflichtigeInformationen,
        };
      }
      case GlobalActions.SET_SHOW_TRANSFER_TO_AGENT_PAGE: {
        return { ...lastState,
          toolbarIsInteractive: !action.payload.showTransferToAgentPage,
          transientState: {
            ...lastState.transientState,
            isTransferToAgentPage: action.payload.showTransferToAgentPage,
          },
        };
      }
      case GlobalActions.SET_UW_REVIEW_BLOCKS_EDITING: {
        return {
          ...lastState,
          transientState: {
            ...lastState.transientState,
            uwReviewBlocksEditing: action.payload.uwReviewBlocksEditing,
          },
        };
      }
      case GlobalActions.OPEN_FORWARDED_TO_UNDERWRITER_PAGE: {
        return { ...lastState, toolbarIsInteractive: false };
      }
      case GlobalActions.CLOSE_FORWARDED_TO_UNDERWRITER_PAGE: {
        return { ...lastState, toolbarIsInteractive: true };
      }
      default:
        return reducer(lastState, action);
    }
  };

function isGesamtpreisInfoAllowed(
  lastPosition: ProgressbarItemsEnum,
  newPosition: ProgressbarItemsEnum,
): boolean {
  return !(
    (newPosition === ProgressbarItemsEnum.ANGEBOT &&
      lastPosition === ProgressbarItemsEnum.EINGABE) ||
    (newPosition === ProgressbarItemsEnum.EMPTY && lastPosition === ProgressbarItemsEnum.ANGEBOT)
  );
}

function shouldShowGesamtpreisInfo(
  lastState: IAppState,
  newState: IAppState,
  actionPayload: GesamtpreisChanged,
): boolean {
  return (
    lastState.gesamtpreis !== newState.gesamtpreis &&
    newState.gesamtpreis !== 0 &&
    lastState.gesamtpreis !== 0 &&
    actionPayload.showGesamtpreisInfo &&
    newState.gesamtpreisPopover.gesamtpreisInfoAllowed &&
    !newState.transientState.zahlungTooltipOpen &&
    !newState.transientState.vnTooltipOpen
  );
}

function shouldHideGesamtpreis(aktivesMenuItem: ProgressbarItemsEnum): boolean {
  return ProgressbarItemsEnum.EINGABE === aktivesMenuItem || location.href.includes('eingabe');
}

function shouldNotHideGesamtpreis(appId: App): boolean {
  return appId === App.SHOP_DASHBOARD;
}

function withLiveTiergruppeTakeOver(snapshot: IAppState, lastState: IAppState): IAppState {
  const tiergruppeTakeOverRunning = lastState.transientState?.tiergruppeTakeOverRunning === true;
  if ((snapshot.transientState?.tiergruppeTakeOverRunning === true) === tiergruppeTakeOverRunning) {
    return snapshot;
  }
  return {
    ...snapshot,
    transientState: { ...snapshot.transientState, tiergruppeTakeOverRunning },
  };
}
